import { Department } from 'enums/department'
import { STOCK_CARD_TYPE_HALF_COMPLETE, VALUE_OUT } from 'enums/stockCard'
import { ISalesOrder, ISalesOrderProduct } from 'interfaces/ISalesOrder'
import { IStockCard } from 'interfaces/IStockCard'

export type StjSummaryRow = {
  qty: number | string | null
  packagingId: string
  premixId: string
  batchNumber: string
  fillingAmount: number | string | null
  fillingUnit: string
  stock: number | string | null
}

const toPositiveNumberOrNull = (value?: number | string | null) => {
  const numericValue = Number(value)
  return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : null
}

const isPieceUnit = (unit?: string | null) =>
  ['pcs', 'pc', 'piece', 'pieces'].includes(
    String(unit || '')
      .trim()
      .toLowerCase()
  )

const getFillingUnitForDepartment = (department: Department) =>
  department === Department.OT ? 'pcs' : 'kg'

const getLabelIds = (value?: string | null) =>
  String(value || '')
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean)

const resolveStjSummaryQuantity = (item: any, stockCard: IStockCard) => {
  const confirmedQuantity =
    item?.confirmedQuantity ?? stockCard.confirmedQuantity
  const requestedQuantity = item?.quantity ?? stockCard.quantity
  const confirmedNumber = Number(confirmedQuantity)
  const quantity =
    confirmedQuantity != null &&
    confirmedQuantity !== '' &&
    Number.isFinite(confirmedNumber) &&
    confirmedNumber > 0
      ? confirmedQuantity
      : requestedQuantity ?? 0

  return Math.abs(Number(quantity || 0))
}

const resolveStjSummaryFilling = (
  item: any,
  stockCard: IStockCard,
  salesOrderProduct: ISalesOrderProduct,
  department: Department
) => {
  const itemAmount = toPositiveNumberOrNull(item?.fillingAmount)
  const cardAmount = toPositiveNumberOrNull(stockCard.fillingAmount)
  const productFillingAmount = toPositiveNumberOrNull(
    salesOrderProduct.fillingAmount
  )
  const productVolume = toPositiveNumberOrNull(salesOrderProduct.volume)
  const fillingAmount =
    itemAmount ?? cardAmount ?? productFillingAmount ?? productVolume ?? null

  const itemUnit = String(item?.fillingUnit || '').trim()
  const cardUnit = String(stockCard.fillingUnit || '').trim()
  const productUnit = String(salesOrderProduct.fillingUnit || '').trim()
  const fillingUnit =
    getFillingUnitForDepartment(department) ||
    (itemAmount != null && itemUnit && !isPieceUnit(itemUnit)
      ? itemUnit
      : '') ||
    (cardUnit && !isPieceUnit(cardUnit) ? cardUnit : '') ||
    (productUnit && !isPieceUnit(productUnit) ? productUnit : '') ||
    (fillingAmount != null ? 'kg' : itemUnit || cardUnit || '')

  return { fillingAmount, fillingUnit }
}

const physicallyMatchesSalesOrderProduct = (
  item: any,
  salesOrderProduct: ISalesOrderProduct
) => {
  const packagingId = String(item.packagingId || '').trim()
  const premixId = String(item.premixId || '').trim()
  const targetPackagingId = String(salesOrderProduct.packagingId || '').trim()
  const targetPremixId = String(salesOrderProduct.premixId || '').trim()
  const itemFillingAmount = Number(item.fillingAmount || 0)
  const targetFillingAmount = Number(
    salesOrderProduct.volume || salesOrderProduct.fillingAmount || 0
  )

  if (packagingId && targetPackagingId && packagingId !== targetPackagingId) {
    return false
  }
  if (premixId && targetPremixId && premixId !== targetPremixId) {
    return false
  }
  if (
    itemFillingAmount &&
    targetFillingAmount &&
    Math.abs(itemFillingAmount - targetFillingAmount) / targetFillingAmount >
      0.1
  ) {
    return false
  }

  return packagingId === targetPackagingId || premixId === targetPremixId
}

const getMatchingSalesOrderProducts = (item: any, salesOrder: ISalesOrder) => {
  const physicalCandidates = salesOrder.salesOrderProducts.filter((product) =>
    physicallyMatchesSalesOrderProduct(item, product)
  )
  const itemLabelIds = getLabelIds(item.labelId)
  if (itemLabelIds.length === 0) {
    return physicalCandidates
  }

  const labelCandidates = physicalCandidates.filter((product) => {
    const targetLabelId = String(product.labelId || '').trim()
    return targetLabelId && itemLabelIds.includes(targetLabelId)
  })

  return labelCandidates.length > 0 ? labelCandidates : physicalCandidates
}

const allocateSharedQuantity = (
  qty: number,
  item: any,
  salesOrder: ISalesOrder,
  salesOrderProduct: ISalesOrderProduct
) => {
  const matchingProducts = getMatchingSalesOrderProducts(item, salesOrder)
  const targetMatches = matchingProducts.some(
    (product) =>
      product.salesOrderProductId === salesOrderProduct.salesOrderProductId
  )
  if (!targetMatches) {
    return null
  }
  if (matchingProducts.length <= 1) {
    return qty
  }

  const totalRequiredQty = matchingProducts.reduce(
    (sum, product) => sum + Number(product.productQty || 0),
    0
  )
  const targetRequiredQty = Number(salesOrderProduct.productQty || 0)
  if (totalRequiredQty <= 0 || targetRequiredQty <= 0) {
    return qty
  }

  return Number(((qty * targetRequiredQty) / totalRequiredQty).toFixed(12))
}

const getHalfCompleteRows = (
  salesOrderProduct: ISalesOrderProduct
): StjSummaryRow[] =>
  salesOrderProduct.halfCompletes?.map((arrival) => ({
    qty: arrival.halfCompleteQty ?? 0,
    packagingId: arrival.halfCompletePackagingId || '-',
    premixId: arrival.halfCompletePremixId || '-',
    batchNumber: arrival.halfCompleteBatchNumber || '-',
    fillingAmount: arrival.halfCompleteFillingAmount,
    fillingUnit: arrival.halfCompleteFillingUnit || '',
    stock: null,
  })) || []

const groupRows = (rows: StjSummaryRow[]) => {
  const groupedRows = new Map<string, StjSummaryRow>()
  rows.forEach((row) => {
    const key = [
      row.packagingId,
      row.premixId,
      row.batchNumber,
      row.fillingAmount ?? '',
      row.fillingUnit || '',
      row.stock ?? '',
    ].join('|')

    const existing = groupedRows.get(key)
    if (existing) {
      existing.qty = Number(existing.qty || 0) + Number(row.qty || 0)
    } else {
      groupedRows.set(key, { ...row })
    }
  })

  return Array.from(groupedRows.values())
}

export const getLabelingStjSummaryRows = (
  salesOrder: ISalesOrder,
  salesOrderProduct: ISalesOrderProduct,
  department: Department
): StjSummaryRow[] => {
  const halfCompleteRows = getHalfCompleteRows(salesOrderProduct).filter(
    (row) => Number(row.qty || 0) > 0
  )
  if (halfCompleteRows.length > 0) {
    return groupRows(halfCompleteRows)
  }

  const requestedRows: StjSummaryRow[] =
    salesOrder.stockCards
      ?.filter(
        (stockCard) =>
          stockCard.type === STOCK_CARD_TYPE_HALF_COMPLETE &&
          stockCard.inOutDirection === VALUE_OUT
      )
      .flatMap((stockCard): StjSummaryRow[] => {
        const stockCardProducts = stockCard.stockCardProducts || []

        if (stockCardProducts.length > 0) {
          return stockCardProducts.flatMap((stockCardProduct) => {
            const { fillingAmount, fillingUnit } = resolveStjSummaryFilling(
              stockCardProduct,
              stockCard,
              salesOrderProduct,
              department
            )
            const rowItem = {
              ...stockCardProduct,
              fillingAmount,
              fillingUnit,
            }
            const qty = allocateSharedQuantity(
              resolveStjSummaryQuantity(stockCardProduct, stockCard),
              rowItem,
              salesOrder,
              salesOrderProduct
            )
            if (qty == null) {
              return []
            }

            return [
              {
                qty,
                packagingId:
                  stockCardProduct.packagingId || stockCard.packagingId || '-',
                premixId:
                  stockCardProduct.premixId || stockCard.premixId || '-',
                batchNumber:
                  stockCardProduct.batchNumber || stockCard.batchNumber || '-',
                fillingAmount,
                fillingUnit,
                stock:
                  stockCardProduct.dynamicLatestStock ??
                  stockCardProduct.latestStock ??
                  stockCardProduct.product?.inventory ??
                  stockCard.dynamicLatestStock ??
                  stockCard.latestStock ??
                  null,
              },
            ]
          })
        }

        const { fillingAmount, fillingUnit } = resolveStjSummaryFilling(
          stockCard,
          stockCard,
          salesOrderProduct,
          department
        )
        const rowItem = {
          ...stockCard,
          fillingAmount,
          fillingUnit,
        }
        const qty = allocateSharedQuantity(
          resolveStjSummaryQuantity(stockCard, stockCard),
          rowItem,
          salesOrder,
          salesOrderProduct
        )
        if (qty == null) {
          return []
        }

        return [
          {
            qty,
            packagingId: stockCard.packagingId || '-',
            premixId: stockCard.premixId || '-',
            batchNumber: stockCard.batchNumber || '-',
            fillingAmount,
            fillingUnit,
            stock: stockCard.dynamicLatestStock ?? stockCard.latestStock,
          },
        ]
      }) || []

  return groupRows(
    requestedRows.length > 0
      ? requestedRows
      : getHalfCompleteRows(salesOrderProduct)
  )
}

export const getStjSummaryRowKey = (row: StjSummaryRow, index: number) =>
  [
    row.packagingId,
    row.premixId,
    row.batchNumber,
    row.fillingAmount ?? '',
    row.fillingUnit || '',
    row.stock ?? '',
    index,
  ].join('|')

export const getStjSummaryTotalQty = (rows: StjSummaryRow[]) =>
  rows.reduce((sum, row) => sum + Number(row.qty || 0), 0)
